// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readConsent, writeConsent, loadGtm, isGtmLoaded, pushPageView, clearAnalyticsCookies } from '@/lib/consent';
import { ConsentBanner } from '@/components/common/ConsentBanner';

// T-h2 : le pied de page public ne sert qu'aux pages publiques (vitrine,
// légal). La connexion et le portail client ont leur propre chrome.
const FOOTER_PAGES = new Set(['Home', 'confidential', 'cgu']);

// DES-14 : la bannière de consentement ne s'affiche que sur les pages
// publiques. Ailleurs, GTM ne se charge que si le choix « accepter » a déjà
// été fait sur l'une d'elles.
export const CONSENT_PAGES = new Set(['Home', 'confidential', 'cgu', 'developers', 'Login']);

export default function Layout({ children, currentPageName }) {
        const { t } = useTranslation(['common', 'pages']);
        const [consent, setConsent] = useState(readConsent);

        const showFooter = FOOTER_PAGES.has(currentPageName);
        const showBanner = consent === null && CONSENT_PAGES.has(currentPageName);
        // GTM never runs on the dialer, and elsewhere only after consent (B3-home).
        const onDialer = (currentPageName === 'ParallelDialer') || (typeof window !== 'undefined' && window.location.pathname.toLowerCase().includes('/paralleldialer'));
        const gtmAllowed = consent === 'granted' && !onDialer;

  useEffect(() => {
    if (!gtmAllowed) return;
    try { loadGtm(); } catch (_e) { /* GTM is non-blocking */ }
  }, [gtmAllowed]);

  const choose = (choice) => {
    writeConsent(choice);
    setConsent(choice);
    // A loaded GTM cannot be unloaded: refusing while it runs reloads the
    // page. Whatever the previous state: « Gérer les cookies » resets it to
    // null before the refusal, while GTM keeps running (REV-W6-F1).
    // QA-W6-3: the cookies GTM's tags wrote are expired first; the reload
    // then starts without the tags that would write them again.
    if (choice === 'denied') {
      clearAnalyticsCookies();
      if (isGtmLoaded()) window.location.reload();
    }
  };

  // A visitor who refused earlier may still carry cookies from before
  // (QA-W6-3): expire them on every page load.
  useEffect(() => {
    if (consent === 'denied') clearAnalyticsCookies();
  }, [consent]);

  const reopenConsent = () => {
    writeConsent(null);
    setConsent(null);
  };

  // Preconnect critical third-parties for faster DNS/TLS
  useEffect(() => {
    try {
      const add = (rel, href, crossOrigin = '') => {
        if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
        const l = document.createElement('link');
        l.rel = rel;
        l.href = href;
        if (crossOrigin) l.crossOrigin = crossOrigin;
        document.head.appendChild(l);
      };
      add('preconnect', 'https://qtrypzzcjebvfcihiynt.supabase.co', 'anonymous');
      add('dns-prefetch', 'https://qtrypzzcjebvfcihiynt.supabase.co');
      add('preconnect', 'https://videos.pexels.com', 'anonymous');
      add('dns-prefetch', 'https://videos.pexels.com');
    } catch (_e) {}
  }, []);

  // Push a page_view on page changes (only once GTM is loaded)
  useEffect(() => {
    if (!gtmAllowed) return;
    try { pushPageView(currentPageName || document.title || ''); } catch (_e) {}
  }, [currentPageName, gtmAllowed]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">{children}</div>

      {/* Pied de page public : seulement sur FOOTER_PAGES (vitrine et pages
          légales). Ni la connexion ni le portail client (T-h2), ni le
          DialerLayout qui a son propre cadre. */}
      {showFooter && (
        <footer className="border-t border-gray-200 bg-white py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500">
            <p>{t('common:footer.copyright', { year: new Date().getFullYear() })}</p>
            <nav className="flex items-center gap-6">
              <a href="/confidential" className="hover:text-gray-900 transition-colors">
                {t('pages:legal.privacyPolicy.title')}
              </a>
              <a href="/cgu" className="hover:text-gray-900 transition-colors">
                {t('pages:legal.termsOfService.shortTitle')}
              </a>
              <button type="button" onClick={reopenConsent} className="hover:text-gray-900 transition-colors">
                {t('common:consent.manage')}
              </button>
            </nav>
          </div>
        </footer>
      )}

      {showBanner && <ConsentBanner onChoice={choose} />}
    </div>
  );
}